import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";
import { api } from "../lib/api";
import { useTitle } from "../App";

const PRACTICES = [
  { n: "01", title: "Accountability in writing", text: "Plans, budgets and expected outcomes are documented before work begins. When something misses, the record says so — and so do we." },
  { n: "02", title: "Curiosity with a job to do", text: "We explore new channels, tools and product ideas constantly, but exploration ends in a decision: adopt, park or discard." },
  { n: "03", title: "Purposeful execution", text: "Every venture and every campaign exists to serve someone specific. If an activity cannot be traced back to that person, it gets questioned." },
  { n: "04", title: "Honest staging", text: "Ideas are labelled as ideas and live work as live work. This site follows the same rule — nothing here is claimed before it is real." },
];

export default function About() {
  useTitle("About");
  const [config, setConfig] = useState({});
  useEffect(() => {
    api.get("/content/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page" className="bg-midnight text-mist">
      <Navbar />
      <section className="grain relative overflow-hidden pt-[76px]">
        <div className="container-ek py-24 sm:py-32">
          <Reveal>
            <p className="eyebrow mb-4">About Ekavrix</p>
            <h1 className="max-w-4xl font-display text-4xl font-bold tracking-tight sm:text-6xl">AI-first by design. Business-led by execution.</h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Ekavrix Tech Ventures is an AI-first venture company creating technology products, intelligent marketing solutions, strategic partnerships and consumer businesses. Four pillars, one technology philosophy: AI &amp; Technology, Marketing Intelligence &amp; Performance, Affiliate &amp; Strategic Partnerships, and Consumer Ventures.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Artificial Intelligence sits at the core of how Ekavrix builds, operates, automates and scales its ventures — applied differently in each business, never exaggerated, always in service of execution.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 text-slatetext sm:py-32">
        <div className="container-ek grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-violet/20 to-teal/15 blur-2xl" />
              <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-[1.6rem] border border-midnight/10 bg-gradient-to-br from-deep to-midnight">
                {(config.founder_photo_about || config.founder_photo) ? (
                  <img src={config.founder_photo_about || config.founder_photo} alt={`${config.founder_name || "Deepak Chanchal"}, founder of Ekavrix Tech Ventures`} className="absolute inset-0 h-full w-full object-cover object-top" />
                ) : (
                  <span className="font-display text-8xl font-bold text-white/90">
                    {(config.founder_name || "Deepak Chanchal").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/70 to-transparent" />
                <div className="absolute bottom-6 left-6">
                  <p className="font-display text-lg font-semibold text-white">{config.founder_name || "Deepak Chanchal"}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">Founder, Ekavrix</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="eyebrow mb-4 !text-violet">Founder</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">Thirteen years of watching what actually works.</h2>
            <p className="mt-6 text-base leading-relaxed text-slatetext/75">{config.founder_bio}</p>
            <p className="mt-4 text-base leading-relaxed text-slatetext/75">
              That is the difference clients feel: Ekavrix is led by someone who has sat on the brand side of the table, answered for the numbers, and built teams that deliver. Every engagement carries that standard — clear plans, honest reporting and growth you can actually point to.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {["AI & Technology", "Marketing Intelligence & Performance", "Affiliate & Strategic Partnerships", "Consumer Ventures"].map((p) => (
                <div key={p} className="rounded-xl border border-midnight/[0.08] bg-white px-5 py-4">
                  <p className="font-display text-sm font-semibold text-midnight">{p}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="container-ek">
          <Reveal>
            <p className="eyebrow mb-4">Working Principles</p>
            <h2 className="max-w-2xl font-display text-3xl font-semibold tracking-tight sm:text-5xl">How the group operates.</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {PRACTICES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div data-testid={`practice-${p.n}`} className="card-dark h-full p-8">
                  <span className="font-mono text-xs text-teal">{p.n}</span>
                  <h3 className="mt-4 font-display text-xl font-semibold">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-14 text-center">
            <Link to="/contact" data-testid="about-cta" className="btn-primary">Work With Us <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
